import type { BookingNote, ExternalBookingNote, PotentialChildMatch } from './types';

async function handleResponse(response: Response): Promise<void> {
    if (!response.ok) {
        throw new Error(`API error ${response.status}`);
    }
}

export async function findByKindergarten(kindergartenId: number, schoolYearId: number): Promise<BookingNote[]> {
    const response = await fetch(`/api/booking-note-sync/kindergartens/${kindergartenId}/internal/${schoolYearId}`, {
        headers: { Accept: 'application/json' },
    });

    await handleResponse(response);

    const json = await response.json() as { data: BookingNote[] };
    return json.data;
}

export async function loadExternalsViaApi(kindergartenId: number, schoolYearId: number): Promise<ExternalBookingNote[]> {
    const response = await fetch(`/api/booking-note-sync/kindergartens/${kindergartenId}/external/${schoolYearId}`, {
        headers: { Accept: 'application/json' },
    });

    await handleResponse(response);

    const json = await response.json() as { data: ExternalBookingNote[] };
    return json.data;
}

export async function createChildAndParentUserAndBookingNote(kindergartenId: number, externalBookingNote: ExternalBookingNote): Promise<void> {
    const response = await fetch(`/api/booking-note-sync/kindergartens/${kindergartenId}/import`, {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ externalBookingNote }),
    });

    await handleResponse(response);
}

export async function updateBookingNote(bookingNoteId: number, externalBookingNote: ExternalBookingNote): Promise<void> {
    const response = await fetch(`/api/booking-note-sync/booking-notes/${bookingNoteId}`, {
        method: 'PUT',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ externalBookingNote }),
    });

    await handleResponse(response);
}

export async function findMatchingChildren(
    kindergartenId: number,
    firstName: string,
    lastName: string,
    dateOfBirth: string,
): Promise<PotentialChildMatch[]> {
    const params = new URLSearchParams({ firstName, lastName, dateOfBirth });
    const response = await fetch(`/api/booking-note-sync/kindergartens/${kindergartenId}/children?${params}`, {
        headers: { Accept: 'application/json' },
    });

    await handleResponse(response);

    const json = await response.json() as { data: PotentialChildMatch[] };
    return json.data;
}

export async function uploadExcelFile(kindergartenId: number, file: File): Promise<ExternalBookingNote[]> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(
        `/api/booking-note-sync/kindergartens/${kindergartenId}/excel/upload`,
        {
            method: 'POST',
            body: formData,
        },
    );

    await handleResponse(response);

    const json = await response.json() as { data: ExternalBookingNote[] };
    return json.data;
}

export interface ExcelMetadata {
    filename: string | null
    uploadedAt: string | null
    entryCount: number
}

export async function getExcelMetadata(kindergartenId: number): Promise<ExcelMetadata> {
    const response = await fetch(`/api/booking-note-sync/kindergartens/${kindergartenId}/excel`, {
        headers: { Accept: 'application/json' },
    });

    await handleResponse(response);

    const json = await response.json() as { data: ExcelMetadata };
    return json.data;
}

export async function importAllBookingNotes(kindergartenId: number): Promise<void> {
    const response = await fetch(`/api/booking-note-sync/kindergartens/${kindergartenId}/import/all`, {
        method: 'POST',
        headers: {
            Accept: 'application/json',
        },
    });

    await handleResponse(response);
}
